import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Dialog, IconButton, Stack, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { DeleteOutline as DeleteIcon, EditOutlined as EditIcon, PhotoCameraOutlined as CameraIcon } from '@mui/icons-material';
import PremiumCard from 'components/shared/surfaces/PremiumCard';
import { PARAM_DEFS } from 'common/constants/physicalParams';

/**
 * Card de una entrada de progreso físico (3 fotos + parámetros + nota). Reutilizable por el
 * cliente (con borrar) y por el entrenador (solo lectura: onDelete null).
 */
function ProgressEntryCard({ entry, onEdit = null, onDelete = null }) {
  const [zoom, setZoom] = useState(null);
  const dateLabel = entry.taken_at || (entry.created_at || '').slice(0, 10);
  const chips = PARAM_DEFS.filter((p) => entry[p.key] != null).map((p) => `${p.label} ${entry[p.key]}${p.unit ? ` ${p.unit}` : ''}`);

  // 3 fotos (frente/lateral/espalda); para entradas viejas cae a antes/después.
  const imgs = [
    entry.front_image_url ? { src: entry.front_image_url, label: 'FRENTE' } : null,
    entry.lateral_image_url ? { src: entry.lateral_image_url, label: 'LATERAL' } : null,
    entry.back_image_url ? { src: entry.back_image_url, label: 'ESPALDA' } : null,
    entry.before_image_url ? { src: entry.before_image_url, label: 'ANTES' } : null,
    entry.after_image_url ? { src: entry.after_image_url, label: 'DESPUÉS' } : null
  ].filter(Boolean);

  const photo = (src, label) => (
    <Box
      onClick={() => setZoom(src)}
      sx={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', backgroundColor: 'surface.raised', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160, maxHeight: 360 }}>
      <Box component="img" src={src} alt={label} sx={{ width: '100%', maxHeight: 360, objectFit: 'contain', display: 'block' }} />
      <Chip size="small" label={label} sx={{ position: 'absolute', top: 8, left: 8, backgroundColor: 'rgba(0,0,0,0.55)', color: '#FFFFFF', fontWeight: 700, letterSpacing: '0.06em' }} />
    </Box>
  );

  return (
    <PremiumCard innerSx={{ p: { xs: 2, md: 2.5 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 800 }}>{dateLabel}</Typography>
        <Stack direction="row" spacing={0.5}>
          {onEdit && <IconButton size="small" onClick={() => onEdit(entry)} sx={{ color: 'secondary.main' }} aria-label="editar"><EditIcon fontSize="small" /></IconButton>}
          {onDelete && <IconButton size="small" onClick={() => onDelete(entry.id)} sx={{ color: 'error.main' }} aria-label="eliminar"><DeleteIcon fontSize="small" /></IconButton>}
        </Stack>
      </Stack>
      {imgs.length > 0 && (
        <Grid container spacing={1.5} justifyContent="center">
          {imgs.map((im) => (
            <Grid xs={6} sm={4} key={im.label}>{photo(im.src, im.label)}</Grid>
          ))}
        </Grid>
      )}
      {/* Sin fotos y con permiso de editar: una invitación a añadirlas, no un hueco. */}
      {imgs.length === 0 && onEdit && (
        <Box
          role="button"
          tabIndex={0}
          aria-label="Añadir fotos a este registro"
          onClick={() => onEdit(entry)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onEdit(entry);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            p: 2,
            borderRadius: '12px',
            border: '1px dashed',
            borderColor: 'hairline.main',
            color: 'text.secondary',
            cursor: 'pointer',
            transition: 'transform 200ms cubic-bezier(0.32, 0.72, 0, 1), background-color 200ms',
            '&:hover': { backgroundColor: 'surface.raised' },
            '&:active': { transform: 'scale(0.98)' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: 2 }
          }}>
          <CameraIcon fontSize="small" sx={{ color: 'gold.main', flexShrink: 0 }} />
          <Typography variant="body2">
            Este registro no tiene fotos. Toca para añadir frente, lateral y espalda.
          </Typography>
        </Box>
      )}
      {chips.length > 0 && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.75 }}>
          {chips.map((c) => <Chip key={c} size="small" label={c} sx={{ backgroundColor: 'gold.soft', color: 'gold.main', fontWeight: 600 }} />)}
        </Stack>
      )}
      {entry.note && <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>{entry.note}</Typography>}

      <Dialog open={Boolean(zoom)} onClose={() => setZoom(null)} maxWidth="md" PaperProps={{ sx: { backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none' } }}>
        {zoom && <Box component="img" src={zoom} alt="" onClick={() => setZoom(null)} sx={{ width: '100%', maxHeight: '85vh', objectFit: 'contain', borderRadius: '14px', display: 'block', cursor: 'zoom-out' }} />}
      </Dialog>
    </PremiumCard>
  );
}

ProgressEntryCard.propTypes = { entry: PropTypes.object.isRequired, onEdit: PropTypes.func, onDelete: PropTypes.func };

export default ProgressEntryCard;
